import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Building2, User, Mail, Lock, Eye, EyeOff } from 'lucide-react'

const TIME_ZONES = [
  { value: 'Eastern Time (US & Canada)', label: 'Eastern Time (US & Canada)' },
  { value: 'Central Time (US & Canada)', label: 'Central Time (US & Canada)' },
  { value: 'Mountain Time (US & Canada)', label: 'Mountain Time (US & Canada)' },
  { value: 'Pacific Time (US & Canada)', label: 'Pacific Time (US & Canada)' },
  { value: 'Alaska', label: 'Alaska' },
  { value: 'Hawaii', label: 'Hawaii' },
  { value: 'Arizona', label: 'Arizona' },
  { value: 'UTC', label: 'UTC' }
]

function AccountCreation({ onSubmit }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      facilityName: '',
      fullName: '',
      preferredName: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      timeZone: 'Eastern Time (US & Canada)',
      website: '',
      teamMascot: '',
      teamFounder: ''
    }
  })

  const password = watch('password')

  async function handleFormSubmit(data) {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  function validatePasswordConfirmation(value) {
    if (!password) return true
    return value === password || 'Passwords do not match'
  }

  return (
    <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
      <Card className="w-full max-w-2xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-6 h-6 text-purple-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Create Your Facility</CardTitle>
          <CardDescription>Get started by creating your account and naming your facility</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="absolute left-[-9999px]" aria-hidden="true" tabIndex={-1}>
              <label htmlFor="website">Leave this field empty</label>
              <input type="text" id="website" autoComplete="off" tabIndex={-1} {...register('website')} />
              <label htmlFor="teamMascot">Team Mascot</label>
              <input type="text" id="teamMascot" autoComplete="off" tabIndex={-1} {...register('teamMascot')} />
              <label htmlFor="teamFounder">Team Founder</label>
              <input type="text" id="teamFounder" autoComplete="off" tabIndex={-1} {...register('teamFounder')} />
            </div>

            <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
              <h3 className="font-medium text-purple-900 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Facility Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="facilityName">
                    Facility Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="facilityName"
                    placeholder="Ice Arena, Dance Studio, etc."
                    autoFocus
                    {...register('facilityName', { required: 'Facility name is required' })}
                    className={errors.facilityName ? 'border-red-500' : ''}
                  />
                  {errors.facilityName && <p className="text-sm text-red-600">{errors.facilityName.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeZone">
                    Time Zone <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="timeZone"
                    control={control}
                    rules={{ required: 'Time zone is required' }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className={errors.timeZone ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select time zone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_ZONES.map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.timeZone && <p className="text-sm text-red-600">{errors.timeZone.message}</p>}
                </div>
              </div>
            </div>

            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <User className="w-4 h-4" />
                Your Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    placeholder="John Smith"
                    autoComplete="name"
                    {...register('fullName', { required: 'Full name is required' })}
                    className={errors.fullName ? 'border-red-500' : ''}
                  />
                  {errors.fullName && <p className="text-sm text-red-600">{errors.fullName.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredName">
                    Preferred Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="preferredName"
                    placeholder="John"
                    {...register('preferredName', { required: 'Preferred name is required' })}
                    className={errors.preferredName ? 'border-red-500' : ''}
                  />
                  {errors.preferredName && <p className="text-sm text-red-600">{errors.preferredName.message}</p>}
                </div>
              </div>
            </div>

            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Account Credentials
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      autoComplete="email"
                      className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative flex items-center">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...register('password', {
                          required: 'Password is required',
                          minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters'
                          }
                        })}
                        className={`pr-10 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden ${
                          errors.password ? 'border-red-500' : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-0 h-full px-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-sm text-red-600">{errors.password.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="passwordConfirmation">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative flex items-center">
                      <Input
                        id="passwordConfirmation"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...register('passwordConfirmation', {
                          required: 'Please confirm your password',
                          validate: validatePasswordConfirmation
                        })}
                        className={`pr-10 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden ${
                          errors.passwordConfirmation ? 'border-red-500' : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-0 h-full px-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.passwordConfirmation && (
                      <p className="text-sm text-red-600">{errors.passwordConfirmation.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full bg-purple-600 hover:bg-purple-700">
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating your facility...
                </>
              ) : (
                'Create Facility & Continue'
              )}
            </Button>

            <p className="text-xs text-center text-gray-500">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </p>

            <div className="text-center pt-4 border-t border-gray-100">
              <a href="/" className="text-sm text-gray-500 hover:text-purple-600">
                ← Back to Home
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

AccountCreation.propTypes = {
  onSubmit: PropTypes.func.isRequired
}

export default AccountCreation
